import { createProjectFromApi, } from 'api/model/project';
import { checkObjectsMatch, sleep } from 'libs/utils';
import camelcaseKeys from 'camelcase-keys';
import { blob, hasErrorMessage, json, text, empty, } from '@newtontechnologies/beey-api-js-client/receivers';
import { apiV2 } from '@newtontechnologies/beey-api-js-client/endpoints';
import { serializeToVttFormat } from 'libs/vtt-format-parser';
import { Result } from 'monadix/result';
import dayjs from 'dayjs';
import { BeeyLocale } from 'libs/locales/locale';
import { parseTrsx } from '@newtontechnologies/beey-trsx-js';
import { subSystemList } from './websocket-api';
const PAGE_LIMIT = (2 ** 31) - 1;
export const isSearchingProjectsQuery = (query) => ('searching' in query && query.searching === true);
export const isSearchedProject = (project) => ('searched' in project && project.searched === true);
const buildProjectsList = (apiList, query) => {
    const items = apiList.list.map((project) => (Object.assign(Object.assign({}, createProjectFromApi(project)), { queued: apiList.queued.includes(project.id), processingStateDetail: project.processingState === 'InProgress'
            ? {
                uploading: apiList.uploading.includes(project.id),
                transcribed: apiList.transcribing.includes(project.id) ? 'unknown' : 'none',
            }
            : null })));
    return {
        items,
        query,
        totalCount: apiList.totalCount,
    };
};
const buildSearchedProjectsList = (apiList, query) => {
    const simpleList = buildProjectsList(apiList, query);
    const items = apiList.list.map((project, index) => (Object.assign(Object.assign({}, simpleList.items[index]), { searched: true, notesHighlight: project.notesHighlight, projectNameHighlight: project.projectNameHighlight, speakerHighlight: project.speakerHighlight, transcriptionHighlight: project.transcriptionHighlight })));
    return {
        items,
        query,
        totalCount: apiList.totalCount,
    };
};
const fetchBasicProjectsList = async (connection, query) => {
    const { currentPage = 1, pageSize = PAGE_LIMIT, orderon = 'created', orderby = 'descending', tags = null, createdFrom, createdTo, updatedFrom, updatedTo, } = query;
    const url = apiV2.projects.search.url({
        skip: pageSize * (currentPage - 1),
        count: pageSize,
        orderon,
        orderby,
        from: updatedFrom,
        to: updatedTo,
        createdFrom,
        createdTo,
        tag: tags,
    });
    return buildProjectsList(await connection.fetch()
        .url(url)
        .post()
        .receive(json())
        .fetch(), query);
};
export const fetchSearchedProjectsList = async (connection, query) => {
    const { currentPage = 1, pageSize = PAGE_LIMIT, orderon = 'created', orderby = 'descend', tags = null, all = '', projectname = '', transcription = '', speakers = '', notes = '', createdFrom = '', createdTo = '', updatedFrom = '', updatedTo = '', } = query;
    const url = apiV2.projects.fullTextSearch.url({
        allFullText: all,
        ProjectNameFullText: projectname,
        TranscriptionFullText: transcription,
        SpeakerFullText: speakers,
        NotesFullText: notes,
        Skip: pageSize * (currentPage - 1),
        Count: pageSize,
        Orderon: orderon,
        Orderby: orderby,
        UpdatedFrom: updatedFrom,
        UpdatedTo: updatedTo,
        CreatedFrom: createdFrom,
        CreatedTo: createdTo,
        tag: tags,
    });
    return buildSearchedProjectsList(await connection.fetch()
        .url(url)
        .post()
        .receive(json())
        .fetch(), query);
};
export const fetchProjectsList = async (connection, query) => (isSearchingProjectsQuery(query)
    ? fetchSearchedProjectsList(connection, query)
    : fetchBasicProjectsList(connection, query));
export const fetchProject = (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).url())
    .receive(json()
    .map(createProjectFromApi)
    .catchHttpError((response) => {
    if (response.status === 404 || response.status === 403) {
        return { error: 'not-found' };
    }
    return 'unknown';
}))
    .fetch();
const withAccessToken = (url, accessToken) => {
    const modifiedUrl = new URL(url);
    modifiedUrl.searchParams.set('accessToken', String(accessToken));
    return modifiedUrl.toString();
};
/* NOTE: Update project with retry on access token conflict.
   If server project matches expectedProjectMask, conflict is automatically
   resolved. Otherwise ApiError is raised. Values omitted
   in expectedProjectMask are not checked. Provide empty project to ignore
   force update without checking for conflicts.
*/
export const safelyUpdateProject = async (connection, url, project, projectFetch, expectedProjectMask = {}) => {
    const result = await projectFetch.base
        .url(withAccessToken(url, project.accessToken))
        .receive(projectFetch.receiver
        .finally((response) => {
        if (response.status === 409) {
            return 'conflict';
        }
        return 'unexpected';
    }))
        .fetchStrict();
    if (result.isSuccess()) {
        return result;
    }
    if (result.isFail()) {
        if (result.err() === 'conflict') {
            const serverProject = await fetchProject(connection, project.id);
            if (serverProject === 'not-found') {
                return Result.fail('not-found');
            }
            if (checkObjectsMatch(serverProject, expectedProjectMask)) {
                global.logger.info('AccessToken conflict resolved automatically');
                return projectFetch.base
                    .url(withAccessToken(url, serverProject.accessToken))
                    .receive(projectFetch.receiver)
                    .fetchStrict();
            }
        }
        else {
            return Result.fail(result.err());
        }
    }
    return Result.fail('unexpected');
};
export const setupNewProject = (connection, projectName) => connection.fetch()
    .url(apiV2.projects.url())
    .postJson({
    Name: projectName,
    CustomPath: '/',
})
    .receive(json().map(createProjectFromApi))
    .fetch();
export const fetchProjectMetadata = async (connection, projectId, key) => {
    const metadata = await connection.fetch()
        .url(apiV2.projects.id(projectId).metadata.url({ key }))
        .receive(json())
        .fetch();
    return metadata === null ? null : metadata.value;
};
export const fetchHeadingsMetadata = async (connection, project) => {
    const headingsMetadata = await fetchProjectMetadata(connection, project.id, 'headings');
    if (headingsMetadata === null) {
        return null;
    }
    return headingsMetadata;
};
export const fetchSecondaryProjectId = async (connection, project) => {
    const secondaryProjectId = await fetchProjectMetadata(connection, project.id, 'secondaryProjectId');
    if (secondaryProjectId === null) {
        return null;
    }
    return String(secondaryProjectId);
};
export const addTagToProject = async (connection, project, tag) => {
    const projectFetchBase = connection.fetch()
        .postJson({ Tag: tag });
    const projectReceiver = json().map(createProjectFromApi);
    return safelyUpdateProject(connection, apiV2.projects.id(project.id).tags.url(), project, { base: projectFetchBase, receiver: projectReceiver }, { tags: project.tags });
};
export const deleteTagFromProject = async (connection, project, tag) => {
    const projectFetchBase = connection.fetch()
        .postJson({ Tag: tag })
        .delete();
    const projectReceiver = json().map(createProjectFromApi);
    return (safelyUpdateProject(connection, apiV2.projects.id(project.id).tags.url(), project, { base: projectFetchBase, receiver: projectReceiver }, { tags: project.tags }));
};
export const updateTags = async (connection, project, tags) => {
    const projectFetchBase = connection.fetch()
        .put(JSON.stringify({ Tags: tags }))
        .headers({ 'Content-Type': 'application/json' });
    const projectReceiver = json().map(createProjectFromApi);
    return (safelyUpdateProject(connection, apiV2.projects.id(project.id).tags.url(), project, { base: projectFetchBase, receiver: projectReceiver }, { tags: project.tags }));
};
export const updateProjectMetadata = (connection, project, key, value) => connection.fetch()
    .url(apiV2.projects.id(project.id).metadata.url({
    accessToken: project.accessToken,
    key,
}))
    .postJson(value)
    .receive(json().map(createProjectFromApi))
    .fetch();
export const updateProjectHeadingsMetadata = (connection, project, value) => connection.fetch()
    .url(apiV2.projects.id(project.id).metadata.url({
    accessToken: project.accessToken,
    key: 'headings',
}))
    .postJson(value)
    .receive(json().map(createProjectFromApi)
    .finally(() => 'unexpected'))
    .fetchStrict();
export const uploadTrsx = async (connection, project, trsx, variant, language, etag = null, label) => {
    const lz4 = lz4_require('lz4');
    const { Buffer } = lz4_require('buffer');
    const input = trsx instanceof File
        ? Buffer.from(await trsx.arrayBuffer())
        : Buffer.from(trsx);
    const output = lz4.encode(input, { blockChecksum: false });
    const trsxBlob = new Blob([output], { type: 'application/octet-stream' });
    const result = connection.fetch()
        .url(apiV2.projects.id(project.id).files[variant].url({
        isCompressed: true,
        label,
        language,
    }))
        .put(trsxBlob)
        .headers({ 'If-Match': etag === null ? '""' : etag, 'Content-Type': 'application/octet-stream' })
        .receive(empty().map((_, response) => ({ etag: response.headers.get('Etag') }))
        .catchHttpError((response) => {
        if (response.status === 412) {
            return { error: 'conflict' };
        }
        return 'unknown';
    })
        .finally(() => 'unexpected'))
        .fetchStrict();
    return result;
};
export const fetchTrsx = (connection, projectId, variant, fileId, label) => connection.fetch()
    .url(apiV2.projects.id(projectId).files[variant].url({ fileId, label }))
    .receive(text().map((data, response) => {
    return { content: data, etag: response.headers.get('Etag') };
})
    .catchHttpError((response) => {
    if (response.status === 404) {
        return { error: 'not-found' };
    }
    return 'unknown';
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const shareProject = async (connection, project, email, isReadonly = false) => {
    const projectFetchBase = connection.fetch()
        .postJson({ shareTo: email, isReadonly });
    const projectReceiver = json()
        .catchHttpError((response, errorPayload) => {
        if (response.status === 422) {
            if (hasErrorMessage(errorPayload, 'Given user to share the project with was not found: ShareTo')) {
                return { error: 'invalid-email' };
            }
            if (hasErrorMessage(errorPayload, 'Already shared to given user: shareTo')) {
                return { error: 'already-shared' };
            }
            if (hasErrorMessage(errorPayload, 'Cannot share with given user: ShareTo')) {
                return { error: 'cannot-share' };
            }
        }
        return 'unknown';
    });
    // NOTE This is a workaround for the fact that the server can return conflict
    // which we need to ignore. All conflicts are being skipped.
    return safelyUpdateProject(connection, apiV2.projects.id(project.id).sharings.url(), project, { base: projectFetchBase, receiver: projectReceiver });
};
export const listProjectSharing = async (connection, projectId, userEmail) => {
    const data = await connection.fetch()
        .url(apiV2.projects.id(projectId).sharings.url())
        .receive(json())
        .fetch();
    return data.list
        .map((item) => item.email)
        .filter((email) => email !== userEmail);
};
export const deleteProject = (connection, project) => {
    const projectFetchBase = connection.fetch()
        .delete();
    const projectReceiver = json();
    // NOTE This is a workaround for the fact that the server can return conflict
    // which we need to ignore. All conflicts are being skipped.
    return safelyUpdateProject(connection, apiV2.projects.id(project.id).url(), project, { base: projectFetchBase, receiver: projectReceiver });
};
export const trashProject = async (connection, project) => {
    const projectFetchBase = connection.fetch()
        .post();
    const projectReceiver = json();
    // NOTE This is a workaround for the fact that the server can return conflict
    // which we need to ignore. All conflicts are being skipped.
    return safelyUpdateProject(connection, apiV2.projects.id(project.id).trash.trash.url(), project, { base: projectFetchBase, receiver: projectReceiver });
};
export const getProjectMessages = (connection, projectId, fromId, toId) => connection.fetch()
    .url(apiV2.projects.id(projectId).messageCache.url({
    fromId,
    toId,
    subsystemFilter: subSystemList.join(';'),
}))
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'not-found' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
const buildExportURI = (projectId, format, withTimeStamps, isRightToLeft, dateTimeOffset, exportTemplateId) => (apiV2.projects.id(projectId).export.url({
    formatId: format, withTimeStamps, exportTemplateId, isRightToLeft, dateTimeOffset,
}));
export const downloadSubtitles = (connection, project, format, length, speakerSignPlacement, pauseBetweenCaptions, autofillPauseBetweenCaptions, upperCaseAllText, isRightToLeft, highlightingMode, unHighlightedColor) => connection.fetch()
    .url(apiV2.projects.id(project.id).export.subtitles.url({
    fileFormatId: format,
    subtitleLineLength: length,
    speakerSignPlacement,
    pauseBetweenCaptionsMs: pauseBetweenCaptions * 1000, // in milisecs
    autofillPauseBetweenCaptionsMs: autofillPauseBetweenCaptions * 1000, // in milisecs
    formattingMode: 'DisableAll',
    makeAllUpperCase: upperCaseAllText,
    isRightToLeft,
    highlightingMode,
    unHighlightedColor,
}))
    .receive(blob()
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchExport = (connection, project, format, withTimeStamps, isRightToLeft, exportTemplateId) => {
    const dateTimeOffset = project.description.start !== null
        ? dayjs(project.description.start).format() : undefined;
    return (connection.fetch()
        .url(buildExportURI(project.id, format, withTimeStamps, isRightToLeft, dateTimeOffset, exportTemplateId))
        .receive(blob())
        .fetch());
};
export const downloadSubtitlesFromLabeled = (connection, project, format, formattingMode, upperCaseAllText, keepInnerLinesStripped, isRightToLeft, highlightingMode, unHighlightedColor) => connection.fetch()
    .url(apiV2.projects.id(project.id).export.subtitles.fromLabeled.url({
    fileFormatId: format,
    keepInnerLinesStripped,
    formattingMode,
    upperCaseAllText,
    isRightToLeft,
    highlightingMode,
    unHighlightedColor,
}))
    .receive(blob()
    .catchHttpError(async (response) => {
    const errorResponse = await response.json();
    const errorPayload = camelcaseKeys(errorResponse, { deep: true });
    if (response.status === 422) {
        switch (true) {
            case hasErrorMessage(errorPayload, 'Subtitle line length must be greater or equal 30.'):
                return { error: 'min-sub-length' };
            case hasErrorMessage(errorPayload, 'Subtitle line length must be less or equal 50.'):
                return { error: 'max-sub-length' };
            case hasErrorMessage(errorPayload, 'Subtitle line length must be a positive integer.'):
                return { error: 'negative-sub-length' };
            case hasErrorMessage(errorPayload, 'Invalid Format Id: FileFormatId'):
                return { error: 'invalid-file-format' };
            default:
                return 'unknown';
        }
    }
    return 'unknown';
})
    .finally(() => 'unexpected'))
    .fetchStrict();
export const convertToSubtitles = async (connection, project, captionParameters) => {
    const body = {
        projectId: Number(project.id),
        subtitleLineLength: captionParameters.maxLineLength,
        keepStripped: captionParameters.keepInnerLinesStripped,
        forceSingleLine: captionParameters.subtitlerMaxLineCount === 1,
        enablePresegmentation: captionParameters.enablePresegmentation,
        speakerSignPlacement: captionParameters.speakerSignPlacement,
        pauseBetweenCaptionsMs: captionParameters.pauseBetweenCaptions * 1000,
        autofillPauseBetweenCaptionsMs: captionParameters.autofillPauseBetweenCaptions * 1000,
        useSpeakerName: captionParameters.useSpeakerName,
        removeNoises: true,
        automaticSpeed: captionParameters.automaticSpeed,
        minLineDurationMs: captionParameters.minDuration * 1000,
        speakerSign: captionParameters.speakerSign,
        feMaxDurationMs: captionParameters.maxDuration * 1000,
        feSpeedWarning: captionParameters.speedWarning,
        feSpeedCriticalWarning: captionParameters.speedCriticalWarning,
        feTemplateName: captionParameters.templateName,
        defaultCaptionPosition: serializeToVttFormat(captionParameters.defaultCaptionPosition),
        defaultFontSize: String(captionParameters.defaultFontSize),
        defaultColor: captionParameters.defaultColor,
        defaultFontName: captionParameters.defaultFontName,
        defaultBackgroundColor: captionParameters.defaultBackgroundColor,
        defaultBackgroundTransparency: captionParameters.defaultBackgroundTransparency,
        upperCaseAllText: captionParameters.upperCaseAllText,
        highlightingMode: captionParameters.highlightingMode,
        unHighlightedColor: captionParameters.unHighlightedColor,
    };
    return connection.fetch()
        .url(withAccessToken(apiV2.projects.id(project.id).export.subtitles.convertToSubtitles.url(), project.accessToken))
        .postJson(body)
        .receive(json()
        .catchHttpError((response, errorPayload) => {
        if (response.status === 422) {
            switch (true) {
                case hasErrorMessage(errorPayload, 'Subtitle line length must be between 15 and 60.'):
                    return { error: 'invalid-sub-length' };
                case hasErrorMessage(errorPayload, 'Subtitle line length must be a positive integer.'):
                    return { error: 'negative-sub-length' };
                case hasErrorMessage(errorPayload, 'Invalid file format.'):
                    return { error: 'invalid-file-format' };
                default:
                    return 'unknown';
            }
        }
        return 'unknown';
    })
        .map(createProjectFromApi))
        .fetchStrict();
};
export const buildMediaManifestUrl = (project, presentationDelay) => apiV2.projects.id(project.id).files.mpdManifest.url({ presentationDelay });
export const buildMediaFileUrl = (project) => apiV2.projects.id(project.id).files.mediaFile.url();
export const buildLowQualityAudioUrl = (project) => (apiV2.projects.id(project.id).lowQualityAudio.url());
export const fetchVideoSceneChange = (project, connection) => connection.fetch()
    .url((apiV2.projects.id(project.id).files.scenes.url()))
    .receive(json()
    .catchHttpError((response) => (response.status === 404 ? { error: 'not-found' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchLastError = (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).lastError.url())
    .receive(json())
    .fetch();
export const createDemoProject = (connection) => connection.fetch()
    .url(apiV2.projects.createDemoProjects.url())
    .receive(json()
    .map((data) => data.projectIds[0])
    .catchHttpError((response) => (response.status === 412 ? { error: 'no-demo-created' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const exportBasicProjects = (connection, query) => {
    const { orderon, orderby = 'descending', tags, createdFrom = '', createdTo = '', updatedFrom = '', updatedTo = '', } = query;
    const url = apiV2.projects.export.list.url({
        OrderOn: orderon,
        OrderBy: orderby,
        From: updatedFrom,
        To: updatedTo,
        CreatedFrom: createdFrom,
        CreatedTo: createdTo,
        Tag: tags,
    });
    return connection.fetch()
        .url(url)
        .receive(blob())
        .fetch();
};
export const exportSearchedProjects = async (connection, query) => {
    const { orderon, orderby = 'descending', tags, all, projectname, transcription, speakers, notes, createdFrom = '', createdTo = '', updatedFrom = '', updatedTo = '', } = query;
    const url = apiV2.projects.export.fullTextSearchList.url({
        AllFullText: all,
        ProjectNameFullText: projectname,
        TranscriptionFullText: transcription,
        SpeakersFullText: speakers,
        NotesFullText: notes,
        OrderOn: orderon,
        OrderBy: orderby,
        From: updatedFrom,
        To: updatedTo,
        CreatedFrom: createdFrom,
        CreatedTo: createdTo,
        Tag: tags,
    });
    return connection.fetch()
        .url(url)
        .receive(blob())
        .fetch();
};
export const exportProjects = async (connection, query) => (isSearchingProjectsQuery(query)
    ? exportSearchedProjects(connection, query)
    : exportBasicProjects(connection, query));
export const calculateTranscriptionCredit = async (connection, ...durations) => (durations.length === 0
    ? []
    : connection.fetch()
        .url(apiV2.projects.calculateCreditToTranscribe.url({ DurationsSeconds: durations }))
        .receive(json().map((data) => data.requiredCredit))
        .fetch());
export const fetchMediaSize = (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).size.url())
    .receive(json())
    .fetch();
export const fetchChecklist = async (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).checklist.url())
    .receive(json())
    .fetch();
export const updateChecklistItem = async (connection, projectId, checklistItem) => connection.fetch()
    .url(apiV2.projects.id(projectId).checklist.id(checklistItem.id).url())
    .putJson(checklistItem)
    .receive(json().catchHttpError((response) => (response.status === 422 ? { error: 'unprocessable-entity' } : 'unknown'))
    .finally(() => 'unexpected'))
    .fetchStrict();
export const fetchProjectRating = (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).rating.url())
    .receive(json().map((data) => data.rating))
    .fetch();
export const addProjectRating = (connection, projectId, rating) => connection.fetch()
    .url(apiV2.projects.id(projectId).rating.url())
    .postJson({ rating })
    .send();
export const unshareProject = (connection, project) => {
    const projectFetchBase = connection.fetch()
        .post();
    connection.fetch();
    const projectReceiver = json();
    // NOTE This is a workaround for the fact that the server can return conflict
    // which we need to ignore. All conflicts are being skipped.
    return safelyUpdateProject(connection, apiV2.projects.id(project.id).unshare.url(), project, { base: projectFetchBase, receiver: projectReceiver });
};
export const fetchProjectFiles = (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).files.url())
    .receive(json())
    .fetch();
export const editProjectDescription = (connection, projectId, description) => connection.fetch()
    .url(apiV2.projects.id(projectId).description.url())
    .patchJson(description)
    .receive(json()
    .catchHttpError((response) => (response.status === 409 ? { error: 'conflict' } : 'unknown'))
    .map(createProjectFromApi)
    .finally(() => 'unexpected'))
    .fetchStrict();
export const updateTranscriptionConfig = (connection, project, language, withDiarization, profile, saveTrsx = true, withPPC = true, withVAD = true, withPunctuation = true, userId = 0, trialTranscription = false, withSpeakerId = false, withUserLex = false, diarizationProfile = null, start = null, duration = null) => connection.fetch()
    .url(withAccessToken(apiV2.projects.id(project.id).transcriptionConfig.url(), project.accessToken))
    .postJson({
    saveTrsx,
    language,
    profile,
    withDiarization,
    withPPC,
    withVAD,
    withPunctuation,
    userId,
    trialTranscription,
    withSpeakerId,
    withUserLex,
    diarizationProfile,
    start,
    duration,
})
    .receive(json().map(createProjectFromApi)
    .finally(() => 'unexpected'))
    .fetchStrict();
export const recalculateSpeakers = async (connection, project, count) => {
    const result = await connection.fetch()
        .url(withAccessToken(apiV2.projects.id(project.id).recalculateSpeakers.url(), project.accessToken))
        .postJson({ count })
        .receive(json().map(createProjectFromApi))
        .fetchStrict();
    if (!result.isSuccess()) {
        return Result.fail('unexpected');
    }
    const trsx = await fetchTrsx(connection, project.id, 'currentTrsx');
    if (!trsx.isSuccess()) {
        return Result.fail('unexpected');
    }
    return Result.success({ project: result.get(), trsx: trsx.get() });
};
const RETRY_DELAYS_SECONDS = [0.5, 1.5];
const fetchCurrentTrsxWithRetry = async (connection, projectId, retryDelaysSeconds) => {
    const trsx = await fetchTrsx(connection, projectId, 'currentTrsx');
    if (trsx.isFail() && trsx.err() === 'not-found' && retryDelaysSeconds.length > 0) {
        await sleep(retryDelaysSeconds[0]);
        return fetchCurrentTrsxWithRetry(connection, projectId, retryDelaysSeconds.slice(1));
    }
    return trsx;
};
export const recalculateSpeakersV2 = async (connection, project) => {
    // apiV2 client does not yet expose RecalculateSpeakersV2 — derive URL from V1
    const url = apiV2.projects.id(project.id).recalculateSpeakers.url()
        .replace('/RecalculateSpeakers', '/RecalculateSpeakersV2');
    const result = await connection.fetch()
        .url(withAccessToken(url, project.accessToken))
        .post()
        .receive(json()
        .map(createProjectFromApi)
        .catchHttpError((response) => (response.status === 412 ? { error: 'no-voiceprints' } : 'unknown'))
        .finally(() => 'unexpected'))
        .fetchStrict();
    if (!result.isSuccess()) {
        return Result.fail(result.err());
    }
    const trsx = await fetchCurrentTrsxWithRetry(connection, project.id, RETRY_DELAYS_SECONDS);
    if (!trsx.isSuccess()) {
        return Result.fail('unexpected');
    }
    return Result.success({ project: result.get(), trsx: trsx.get() });
};
export const getLanguageFromTrsx = (trsx) => {
    const parsedTrsx = parseTrsx(trsx);
    if (parsedTrsx.success) {
        const langCode = parsedTrsx.value.getTrsx().transcriptionMeta.language;
        if (langCode !== undefined) {
            return BeeyLocale.fromCode(langCode);
        }
    }
    return null;
};
export const getProjectLanguage = async (connection, projectId) => {
    var _a;
    let trsxResult = await fetchTrsx(connection, projectId, 'currentTrsx');
    if (trsxResult.isFail() && trsxResult.err() === 'not-found') {
        trsxResult = await fetchTrsx(connection, projectId, 'initialTrsx');
    }
    if (trsxResult.isSuccess()) {
        const trsx = trsxResult.get();
        return getLanguageFromTrsx(trsx.content);
    }
    // NOTE This is fallback for old projects which do not have language in trsx
    const project = await fetchProject(connection, projectId);
    if (project !== 'not-found' && project.transcriptionConfig !== null) {
        return BeeyLocale.fromCode((_a = project.transcriptionConfig.language) !== null && _a !== void 0 ? _a : '');
    }
    return null;
};
export const fetchTrsxLabels = (connection, projectId) => connection.fetch()
    .url(apiV2.projects.id(projectId).files.trsxLabels.url())
    .receive(json())
    .fetch();
export const updateActiveTrsxLabel = (connection, project, label) => connection.fetch()
    .url(withAccessToken(apiV2.projects.id(project.id).activeTrsxLabel.url(), project.accessToken))
    .postJson({ label })
    .receive(json().map(createProjectFromApi))
    .fetch();
